import { ProjectsRepository } from './ProjectsRepository';
import { SelectionConverter, SelectionRepository } from './SelectionRepository';
import { Project, SelectionLike } from './types';

/**
 * The reusable "default project" logic every app needs to add and persist selections.
 *
 * It owns no storage or framework glue: it drives an injected {@link ProjectsRepository}
 * (which in turn wraps an injected store). Both the SAS app and the web-app bind their own
 * store + region and then use these methods to load, add and persist selections identically.
 */
export class DefaultProjectSelections<TSelection extends SelectionLike, TView = TSelection> {
    public constructor(
        private readonly projects: ProjectsRepository<TSelection>,
        private readonly namePrefix: string = 'Selection',
        private readonly convert?: SelectionConverter<TSelection, TView>,
    ) {
    }

    /** Load the default project, creating it on first use. */
    public async load(): Promise<Project<TSelection>> {
        return this.projects
            .get(this.projects.getDefaultProjectName())
            .catch(() => this.projects.init());
    }

    /** Build an in-memory repository over a project's selections. */
    public toRepository(project: Project<TSelection>): SelectionRepository<TSelection, TView> {
        return new SelectionRepository<TSelection, TView>(project.selections, this.convert);
    }

    /** Persist a new selection list onto the given project. */
    public async persist(project: Project<TSelection>, selections: TSelection[]): Promise<Project<TSelection>> {
        const updated: Project<TSelection> = { ...project, selections, updatedAt: new Date() };
        await this.projects.update(updated);
        return updated;
    }

    /** Load the default project, prepend a new selection and persist it. */
    public async addSelection(selection: Omit<TSelection, 'name' | 'createdAt' | 'updatedAt'>): Promise<string> {
        const project = await this.load();
        const repository = this.toRepository(project);
        const name = repository.proposeName(this.namePrefix);
        const now = new Date();
        await this.persist(project, repository.add({
            ...selection,
            name,
            createdAt: now,
            updatedAt: now,
        } as unknown as TSelection));
        return name;
    }
}
